import React, { createContext, useContext, useState, useCallback } from "react";
import { Snackbar, Alert, useMediaQuery, useTheme } from "@mui/material";

// Two contexts on purpose. The setter is referentially stable, so the many
// components that only call setSnackAlert never re-render when a snack shows or
// auto-hides. Only SnackAlert (the display) subscribes to the changing value.
// Bundling both in one value object meant every snack tick re-rendered the
// whole subtree under the provider, which froze heavy views (e.g. the
// term-grade table) when a snack closed while a menu was opening.
const SnackSetterContext = createContext(null);
const SnackValueContext = createContext([]);

export function SnackAlertProvider({ children }) {
  const [snackAlert, setSnackAlertState] = useState([]);

  const setSnackAlert = useCallback((message, severity) => {
    setSnackAlertState([message, severity]);
  }, []);

  return (
    <SnackSetterContext.Provider value={setSnackAlert}>
      <SnackValueContext.Provider value={snackAlert}>
        {children}
      </SnackValueContext.Provider>
    </SnackSetterContext.Provider>
  );
}

export const useSnackAlert = () => {
  const setSnackAlert = useContext(SnackSetterContext);
  if (!setSnackAlert) {
    throw new Error("useSnackAlert must be used within a SnackAlertProvider");
  }
  return { setSnackAlert };
};

export function SnackAlert() {
  const snackAlert = useContext(SnackValueContext);
  const setSnackAlert = useContext(SnackSetterContext);
  const [message, severity] = snackAlert;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Snackbar
      open={Boolean(message)}
      autoHideDuration={6000}
      onClose={() => setSnackAlert(null)}
      anchorOrigin={
        isMobile
          ? { vertical: "top", horizontal: "center" }
          : { vertical: "bottom", horizontal: "left" }
      }
      // On mobile the snack sits below the sticky top navbar (~56px) so it
      // never covers it; desktop keeps the default offset.
      sx={isMobile ? { top: 72 } : undefined}
    >
      <Alert
        onClose={() => setSnackAlert(null)}
        severity={severity}
        sx={{ boxShadow: "0px 4px 17.6px 0px #00000080" }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
}
